import React, { useState, useEffect, useRef } from 'react';
import { useSpaces } from '../context/SpaceContext';
import { uploadFile, getSpaceFiles, getDirectFiles, downloadFile, openFile, deleteFile } from '../services/fileService';
import './FilesPage.css';

const DIRECT_SCOPE = '__direct__';

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function fileGlyph(mimeType = '') {
  if (mimeType.includes('pdf')) return 'PDF';
  if (mimeType.includes('word') || mimeType.includes('officedocument.wordprocessingml')) return 'DOC';
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return 'XLS';
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'PPT';
  if (mimeType.startsWith('image/')) return 'IMG';
  if (mimeType.startsWith('video/')) return 'VID';
  if (mimeType.startsWith('audio/')) return 'AUD';
  if (mimeType.includes('zip') || mimeType.includes('compressed')) return 'ZIP';
  return 'FILE';
}

export default function FilesPage() {
  const { spaces } = useSpaces();
  const [selectedSpaceId, setSelectedSpaceId] = useState('');
  const [files, setFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (spaces.length > 0 && !selectedSpaceId) {
      setSelectedSpaceId(spaces[0]._id);
    }
  }, [spaces, selectedSpaceId]);

  useEffect(() => {
    if (!selectedSpaceId) return;
    setLoading(true);
    setError('');
    const loader = selectedSpaceId === DIRECT_SCOPE
      ? getDirectFiles()
      : getSpaceFiles(selectedSpaceId);

    loader
      .then((res) => setFiles(res.data.files || []))
      .catch(() => setError('Failed to load files.'))
      .finally(() => setLoading(false));
  }, [selectedSpaceId]);

  const handleUpload = async (fileList) => {
    if (!selectedSpaceId || !fileList?.length) return;
    setUploading(true);
    setError('');
    try {
      const isDirect = selectedSpaceId === DIRECT_SCOPE;
      for (const file of fileList) {
        const res = await uploadFile(isDirect ? null : selectedSpaceId, file);
        setFiles((prev) => [res.data.file, ...prev]);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(Array.from(e.dataTransfer.files));
  };

  const handleDownload = async (file) => {
    try {
      await downloadFile(file._id, file.originalName || 'document');
    } catch (err) {
      setError(err?.response?.data?.message || 'Download failed.');
    }
  };

  const handleOpen = async (file) => {
    try {
      await openFile(file._id);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to open file.');
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await deleteFile(fileId);
      setFiles((prev) => prev.filter((f) => f._id !== fileId));
    } catch (err) {
      setError(err?.response?.data?.message || 'Delete failed.');
    }
  };

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredFiles = files.filter((file) => {
    if (!normalizedSearch) return true;

    const haystack = [
      file.originalName,
      file.mimeType,
      file.uploadedBy?.name,
      file.uploadedBy?.email,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return haystack.includes(normalizedSearch);
  });

  return (
    <div className="files-page">
      <div className="files-header">
        <h1>Files</h1>
        <div className="files-controls">
          <select
            className="space-select"
            value={selectedSpaceId}
            onChange={(e) => setSelectedSpaceId(e.target.value)}
          >
            {spaces.length === 0 && <option value="">No spaces</option>}
            <option value={DIRECT_SCOPE}>Direct / Shared</option>
            {spaces.map((s) => (
              <option key={s._id} value={s._id}>{s.name}</option>
            ))}
          </select>
          <input
            type="text"
            className="files-search-input"
            placeholder="Search files by name, type, or uploader..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button
            className="btn btn-primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || !selectedSpaceId}
          >
            {uploading ? 'Uploading...' : '+ Upload'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => handleUpload(Array.from(e.target.files))}
          />
        </div>
      </div>

      {error && <div className="files-error">{error}</div>}

      <div
        className={`drop-zone ${dragOver ? 'drag-over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <span>Drag &amp; drop files here, or use Upload above</span>
      </div>

      {loading ? (
        <p className="files-muted">Loading files...</p>
      ) : filteredFiles.length === 0 ? (
        <p className="files-muted">
          {normalizedSearch
            ? 'No files match your search.'
            : selectedSpaceId === DIRECT_SCOPE
            ? 'No direct/shared files yet.'
            : 'No files in this space yet.'}
        </p>
      ) : (
        <table className="files-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Size</th>
              <th>Type</th>
              <th>Uploaded by</th>
              <th>Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredFiles.map((f) => (
              <tr key={f._id}>
                <td className="file-name">
                  <div className="file-title-wrap">
                    <span className="file-glyph">{fileGlyph(f.mimeType)}</span>
                    <div className="file-title-meta">
                      <button
                        type="button"
                        className="file-title file-open-link"
                        onClick={() => handleOpen(f)}
                        title="Open file"
                      >
                        {f.originalName}
                      </button>
                      <span className="file-title-sub">{f.mimeType || 'Unknown type'}</span>
                    </div>
                  </div>
                </td>
                <td>{formatSize(f.size)}</td>
                <td className="file-mime">{f.mimeType}</td>
                <td>{f.uploadedBy?.name || '—'}</td>
                <td>{formatDate(f.createdAt)}</td>
                <td className="file-actions-cell">
                  <button
                    className="btn-icon btn-download"
                    onClick={() => handleDownload(f)}
                    title="Download file"
                  >
                    ⬇
                  </button>
                  <button
                    className="btn-icon btn-danger"
                    onClick={() => handleDelete(f._id)}
                    title="Delete file"
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
